import { BadgeColor, MUIBadge } from 'components/common/MUIBadge'
import { CampaignPhase, SocialCampaignDTO } from 'models/socialCampaign'
import { EditOutlined, MoreHorizOutlined } from '@mui/icons-material'
import { GRAY_700, GRAY_900 } from 'constants/styling/theme'
import { campaignHeaderMeta, formatCampaignDay, isCampaignRunning } from 'utils/socialCampaign/campaignStatus'
import { forwardRef, useMemo } from 'react'

import { MUIButton } from 'components/common/MUIButton'
import { MUIDropdown } from 'components/common/MUIDropdown'
import { MUIDropdownItem } from 'components/common/MUIDropdownItem'
import { MUISwitch } from 'components/common/MUISwitch'
import { NetworkMark } from './NetworkMark.component'
import Typography from '@mui/material/Typography'
import classnames from 'classnames'
import styles from './CampaignHeader.module.sass'
import { useTranslation } from 'react-i18next'

/** Draft yellow, Active green (live or quiet), Paused gray, Ended beige (prototype v3 `PHASE_BADGE`). */
const PHASE_BADGE: Record<CampaignPhase, { color: BadgeColor, label: 'draft' | 'active' | 'paused' | 'ended' }> = {
  [CampaignPhase.DRAFT]: { color: 'orange', label: 'draft' },
  [CampaignPhase.LIVE]: { color: 'green', label: 'active' },
  [CampaignPhase.QUIET]: { color: 'green', label: 'active' },
  [CampaignPhase.PAUSED]: { color: 'gray', label: 'paused' },
  [CampaignPhase.ENDED]: { color: 'beige', label: 'ended' },
}

/** One header per tab: the menu's id only has to be unique on the page */
const ACTIONS_MENU_ID = 'campaign-actions-menu'

interface Props {
  campaign: SocialCampaignDTO
  now: Date
  /** A pause or resume is on its way: the switch waits for it */
  switchBusy: boolean
  onOpenSettings: () => void
  /** The switch turned off: confirm before pausing */
  onPauseRequest: () => void
  onResume: () => void
  /** "End campaign" in the ⋯ menu: confirm before ending */
  onEndRequest: () => void
}

/**
 * The campaign's header in every state (prototype v3 `campaignHtml` head): the title and its badge,
 * the meta line, then on the right the Pause switch once launched, the networks and language
 * (v3 decision 8; a static summary once ended) and the ⋯ menu holding End campaign. The title takes
 * the focus after the generation or an action that changes the page (`tabIndex={-1}`).
 */
export const CampaignHeader = forwardRef<HTMLHeadingElement, Props>(function CampaignHeader(
  { campaign, now, switchBusy, onOpenSettings, onPauseRequest, onResume, onEndRequest },
  titleRef
) {
  const { t, i18n } = useTranslation(['social_campaign'])
  const { phase } = campaign
  const badge = PHASE_BADGE[phase]
  const running = isCampaignRunning(phase)
  const paused = phase === CampaignPhase.PAUSED

  const meta = useMemo(() => {
    const value = campaignHeaderMeta(campaign, now)
    switch (value.kind) {
      case 'draft':
        return [
          t('header.meta.starts', { date: formatCampaignDay(value.startDay, i18n.language) }),
          t('header.meta.posts', { count: value.posts }),
          ...(value.reserved ? [t('header.meta.reserved', { count: value.reserved })] : []),
        ]
      case 'ended':
        return [t('header.meta.ended'), t('header.meta.published', { count: value.published })]
      case 'running':
        return [
          t('header.meta.published', { count: value.published }),
          t('header.meta.ahead', { count: value.ahead }),
          ...(value.waiting ? [t('header.meta.waiting', { count: value.waiting })] : []),
        ]
    }
  }, [campaign, now, t, i18n.language])

  const networkNames = campaign.networks.map((network) => t(`network.${network}`))
  const settingsSummary = `${networkNames.join(', ') || t('header.settings_none')} · ${t(`language.${campaign.draftLanguage}`)}`
  const settingsContent = (
    <span className={styles.settings}>
      <span className={styles.marks} aria-hidden="true">
        {campaign.networks.length
          ? campaign.networks.map((network) => <NetworkMark key={network} network={network} size={20} />)
          : <span className={styles.none}>{t('header.settings_none')}</span>
        }
      </span>
      <span className={styles.language} aria-hidden="true">{campaign.draftLanguage}</span>
    </span>
  )

  return (
    <div className={styles.header}>

      <div className={styles.titles}>

        <div className={styles.titleRow}>

          <Typography
            ref={titleRef}
            variant="text-xl"
            component="h2"
            tabIndex={-1}
            // `&&` outranks the old-style routes' global h2 size and colour (src/styles/index.sass)
            sx={theme => ({ '&&': { margin: 0, fontSize: theme.typography['text-xl']?.fontSize, lineHeight: theme.typography['text-xl']?.lineHeight, fontWeight: 550, color: GRAY_900, outline: 'none' } })}
          >
            {t('header.title')}
          </Typography>

          <MUIBadge size="md" color={badge.color} label={t(`header.badge.${badge.label}`)} />

        </div>

        <Typography variant="text-sm" component="p" color={GRAY_700}>{meta.join(' · ')}</Typography>

      </div>

      <div className={styles.actions}>

        {running &&
          <MUISwitch
            role="switch"
            checked={!paused}
            disabled={switchBusy}
            label={paused ? t('header.switch_paused') : t('header.switch_publishing')}
            onChange={() => paused ? onResume() : onPauseRequest()}
          />
        }

        {phase === CampaignPhase.ENDED &&
          <span className={classnames(styles.settingsSlot, styles.staticSettings)}>
            {settingsContent}
            <span className={styles.srOnly}>{settingsSummary}</span>
          </span>
        }

        {phase !== CampaignPhase.ENDED &&
          <span className={styles.settingsSlot}>
            <MUIButton
              type="secondaryNoBorder"
              size="sm"
              endIcon={<EditOutlined htmlColor={GRAY_700} />}
              aria-label={t('header.settings_label', { networks: networkNames.join(', ') || t('header.settings_none'), language: t(`language.${campaign.draftLanguage}`) })}
              onClick={onOpenSettings}
            >
              {settingsContent}
            </MUIButton>
          </span>
        }

        {running &&
          <MUIDropdown
            id={ACTIONS_MENU_ID}
            isKeyboardNavigable
            placement="bottom-end"
            button={(isOpen, open) => (
              <MUIButton
                type="secondaryBorder"
                size="sm"
                iconOnly
                aria-label={t('header.actions_menu')}
                aria-haspopup="menu"
                aria-expanded={isOpen}
                aria-controls={isOpen ? ACTIONS_MENU_ID : undefined}
                onClick={open}
              >
                <MoreHorizOutlined />
              </MUIButton>
            )}
          >
            <MUIDropdownItem title={t('header.end')} isDestructive onClick={onEndRequest} />
          </MUIDropdown>
        }

      </div>

    </div>
  )
})
